import { preserveThemePreview, previewLink } from './carben-web-preview.js';

export function nativeCartEndpoint(actionUrl, currentUrl, operation) {
  if (!['add', 'change'].includes(operation)) return null;
  try {
    const current = new URL(currentUrl), target = new URL(actionUrl, current);
    if (target.origin !== current.origin || target.username || target.password) return null;
    if (!new RegExp(`^(?:/[a-zA-Z0-9_-]+)*/cart/${operation}$`).test(target.pathname)) return null;
    target.pathname += '.js';
    target.search = '';
    target.hash = '';
    return target.href;
  } catch { return null; }
}

export function validNativeCart(cart) {
  if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) return false;
  const keys = new Set();
  let count = 0;
  for (const item of cart.items) {
    if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return false;
    keys.add(item.key);
    count += item.quantity;
  }
  return Number.isSafeInteger(count) && count === cart.item_count;
}

export function nativeCartMessage(payload, fallback) {
  if (payload && typeof payload === 'object') {
    for (const value of [payload.description, payload.message, payload.errors]) {
      if (typeof value === 'string' && value.trim()) return value.trim().slice(0, 1200);
      if (value && typeof value === 'object') {
        const messages = Object.values(value).flat().filter(item => typeof item === 'string' && item.trim());
        if (messages.length) return messages.slice(0, 4).join(' ').slice(0, 1200);
      }
    }
  }
  return fallback;
}

export async function requestNativeCart(endpoint, body, fetcher = globalThis.fetch, signal) {
  const response = await fetcher(endpoint, { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' }, signal });
  const payload = await response.json().catch(() => null);
  const isAdd = new URL(endpoint).pathname.endsWith('/cart/add.js');
  const confirmed = isAdd
    ? payload && typeof payload.key === 'string' && payload.key.length > 0 && Number.isSafeInteger(payload.quantity) && payload.quantity > 0
    : validNativeCart(payload);
  return { ok: response.ok && Boolean(confirmed), status: response.status, payload };
}

const commerce = typeof document === 'undefined' ? null : document.querySelector('[data-editorial-commerce]');
if (commerce) {
  preserveThemePreview(document, window);
  let inFlight = false;
  let needsReview = false;

  commerce.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-live-cart-action') || event.defaultPrevented) return;
    const operation = form.getAttribute('data-live-cart-action');
    const endpoint = nativeCartEndpoint(form.action, window.location.href, operation);
    if (!endpoint || form.method.toLowerCase() !== 'post' || typeof fetch !== 'function') return;
    event.preventDefault();
    if (inFlight || needsReview) return;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const body = new FormData(form);
    body.delete('return_to');
    let cartUrl;
    try {
      cartUrl = new URL(form.querySelector('[name="return_to"]')?.value || commerce.getAttribute('data-cart-url') || '/cart', location.href);
      if (cartUrl.origin !== location.origin || cartUrl.username || cartUrl.password || !/^(?:\/[a-zA-Z0-9_-]+)*\/cart$/.test(cartUrl.pathname)) return;
    } catch { return; }
    const cartDestination = previewLink(cartUrl.href, location.href, window.Shopify?.theme);
    const beforeCount = Number(document.querySelector('[data-cart-count]')?.textContent);
    const controls = [...commerce.querySelectorAll('form[data-live-cart-action] button[type="submit"]')].map(button => [button, button.disabled]);
    controls.forEach(([button]) => { button.disabled = true; });
    inFlight = true;
    form.setAttribute('aria-busy', 'true');
    let feedback = form.querySelector('[data-cart-feedback]');
    if (!feedback) {
      feedback = document.createElement('div');
      feedback.className = 'native-cart-feedback';
      feedback.setAttribute('data-cart-feedback', '');
      feedback.tabIndex = -1;
      form.append(feedback);
    }
    feedback.setAttribute('role', 'status');
    feedback.textContent = commerce.getAttribute('data-cart-working-message') || '';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let result;
    try { result = await requestNativeCart(endpoint, body, fetch, controller.signal); }
    catch { result = { ok: false, status: 0, payload: null }; }
    finally { clearTimeout(timeout); }
    if (result.ok) {
      window.location.assign(cartDestination);
      return;
    }
    const fallback = commerce.getAttribute(result.status === 0 || result.status >= 500 || result.status < 400 ? 'data-cart-unknown-message' : 'data-cart-error-message') || '';
    feedback.setAttribute('role', 'alert');
    feedback.textContent = nativeCartMessage(result.payload, fallback);
    const reviewLink = document.createElement('a');
    reviewLink.className = 'text-link';
    reviewLink.href = cartDestination;
    reviewLink.textContent = commerce.getAttribute('data-cart-review-message') || '';
    feedback.append(document.createTextNode(' '), reviewLink);
    needsReview = true;
    const readController = new AbortController();
    const readTimeout = setTimeout(() => readController.abort(), 10000);
    try {
      const readUrl = new URL(cartUrl.href);
      readUrl.pathname += '.js';
      readUrl.search = '';
      readUrl.hash = '';
      const response = await fetch(readUrl.href, { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: readController.signal });
      const cart = response.ok ? await response.json() : null;
      if (validNativeCart(cart)) {
        for (const counter of document.querySelectorAll('[data-cart-count]')) counter.textContent = String(cart.item_count);
        if (result.status === 422) {
          const renderedCart = commerce.querySelector('[data-editorial-cart]');
          const lines = renderedCart ? [...renderedCart.querySelectorAll('[data-line-key]')] : [];
          const unchanged = renderedCart
            ? lines.length === cart.items.length && Number(renderedCart.getAttribute('data-cart-total')) === cart.total_price && lines.every(line => {
              const item = cart.items.find(entry => entry.key === line.getAttribute('data-line-key'));
              const quantity = line.querySelector('input[type="number"][name="quantity"]');
              return item && quantity instanceof HTMLInputElement && item.quantity === Number(quantity.defaultValue);
            })
            : Number.isSafeInteger(beforeCount) && beforeCount === cart.item_count;
          if (unchanged) {
            for (const line of lines) {
              const item = cart.items.find(entry => entry.key === line.getAttribute('data-line-key'));
              const quantity = line.querySelector('input[type="number"][name="quantity"]');
              if (item && quantity instanceof HTMLInputElement) quantity.value = String(item.quantity);
            }
            needsReview = false;
          }
        }
      }
    } catch { /* The mutation is never retried when its final state cannot be confirmed. */ }
    finally { clearTimeout(readTimeout); }
    if (!needsReview) controls.forEach(([button, disabled]) => { button.disabled = disabled; });
    inFlight = false;
    form.setAttribute('aria-busy', 'false');
    feedback.focus();
  });

  commerce.addEventListener('change', event => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || select.name !== 'variant') return;
    const form = select.closest('form[data-editorial-variant-form]');
    if (!form || !/^[1-9][0-9]*$/.test(select.value) || !select.checkValidity()) return;
    form.requestSubmit();
  });

  commerce.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const stepButton = target?.closest('[data-editorial-quantity-step]');
    if (stepButton instanceof HTMLButtonElement && !stepButton.disabled) {
      const quantity = stepButton.closest('[data-editorial-quantity]')?.querySelector('input[name="quantity"]');
      if (!(quantity instanceof HTMLInputElement) || quantity.disabled) return;
      const direction = stepButton.getAttribute('data-editorial-quantity-step');
      if (direction !== '-1' && direction !== '1') return;
      if (!Number.isFinite(quantity.valueAsNumber)) quantity.value = quantity.min || '1';
      try {
        if (direction === '-1') quantity.stepDown();
        else quantity.stepUp();
      } catch { return; }
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const thumbnail = target?.closest('[data-editorial-gallery-src]');
    if (!(thumbnail instanceof HTMLButtonElement)) return;
    const product = thumbnail.closest('[data-editorial-product]');
    const image = product?.querySelector('[data-editorial-main-image]');
    if (!(image instanceof HTMLImageElement)) return;
    let source;
    try {
      source = new URL(thumbnail.getAttribute('data-editorial-gallery-src') || '', location.href);
      if (source.username || source.password || source.port || source.protocol !== 'https:') return;
      if (source.origin !== location.origin && !['cdn.shopify.com', 'carben.mx', 'www.carben.mx'].includes(source.hostname)) return;
    } catch { return; }
    image.removeAttribute('srcset');
    image.src = source.href;
    image.alt = thumbnail.getAttribute('data-editorial-gallery-alt') || '';
    for (const other of product.querySelectorAll('[data-editorial-gallery-src]')) other.setAttribute('aria-pressed', String(other === thumbnail));
  });

  window.addEventListener('pageshow', event => {
    if (event.persisted) window.location.reload();
  });
}
